import { Controller } from '@hotwired/stimulus'
import TomSelect from 'tom-select'
import i18next from 'i18next'

// Connects to data-controller="financial-records--form"
export default class extends Controller {
  static targets = [
    'paidByName', 'paidById', 'paidByType',
    'paidToName', 'paidToId', 'paidToType',
  ]

  static values = { participantsUrl: String }

  // Clients usually pay the agency, while the agency usually pays suppliers.
  static PAID_BY_GROUP_ORDER = ['clients', 'contacts', 'suppliers', 'agency']
  static PAID_TO_GROUP_ORDER = ['suppliers', 'agency', 'contacts', 'clients']

  connect() {
    this.loadParticipants().then(({ options, optgroups }) => {
      this.paidBySelect = this.initContactSelect(
        this.paidByNameTarget,
        this.paidByIdTarget,
        this.paidByTypeTarget,
        options,
        this.sortOptgroups(optgroups, this.constructor.PAID_BY_GROUP_ORDER),
      )
      this.paidToSelect = this.initContactSelect(
        this.paidToNameTarget,
        this.paidToIdTarget,
        this.paidToTypeTarget,
        options,
        this.sortOptgroups(optgroups, this.constructor.PAID_TO_GROUP_ORDER),
      )
    })
  }

  disconnect() {
    this.paidBySelect?.destroy()
    this.paidToSelect?.destroy()
  }

  loadParticipants() {
    return fetch(this.participantsUrlValue, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .catch(() => ({ options: [], optgroups: [] }))
  }

  // Returns copies: TomSelect stamps `$order` on each optgroup object and keeps it on reuse, so
  // sharing the same objects between the two selects would give both the first one's order.
  sortOptgroups(optgroups, order) {
    return optgroups
      .map((optgroup) => ({ ...optgroup }))
      .sort((a, b) => order.indexOf(a.value) - order.indexOf(b.value))
  }

  initContactSelect(nameInput, idInput, typeInput, options, optgroups) {
    const currentValue = nameInput.value.trim()

    const select = new TomSelect(nameInput, {
      valueField: 'full_name',
      labelField: 'full_name',
      searchField: ['full_name'],
      optgroupField: 'group',
      optgroups,
      // Without this TomSelect orders groups by their first option, ignoring `optgroups` order.
      lockOptgroupOrder: true,
      options,
      create: true,
      createOnBlur: true,
      maxItems: 1,
      closeAfterSelect: true,
      onChange: (value) => {
        const option = select.options[value]
        if (option?.id) {
          idInput.value = option.id
          typeInput.value = option.entity_type
        } else {
          idInput.value = ''
          typeInput.value = ''
        }
      },
      render: {
        option: (item, escape) => {
          const bold = item.group === 'agency'
          return `<div class="${bold ? 'fw-bold' : ''}">${escape(item.full_name)}</div>`
        },
        item: (item, escape) => {
          const bold = item.group === 'agency'
          return `<div class="${bold ? 'fw-bold' : ''}">${escape(item.full_name)}</div>`
        },
        option_create: (data, escape) => {
          const label = i18next.t('attributes.base.add_new_value', { value: data.input })
          return `<div class="create"><span class="ms-2">${escape(label)}</span></div>`
        },
        // `option_create` already tells the user how to add a new value, so no need for a
        // separate "no results" message here.
        no_results: () => '',
      },
    })

    // The fetched `options` are the current participants list, which may not include a value
    // saved earlier (eg. a freely typed name). Without this, TomSelect silently drops it and
    // blanks the underlying field on edit.
    if (currentValue && !select.options[currentValue]) {
      select.addOption({ full_name: currentValue, id: idInput.value, entity_type: typeInput.value })
      select.setValue(currentValue, true)
    }

    return select
  }
}
